// 기본 플랫폼 선택 — "열자마자 빈 화면"을 막는다.
//
// 기존엔 세 섹션이 전부 `useState<Platform>('youtube')` 였다. 그런데 협찬 데이터가 TikTok 에만
// 있는 크리에이터를 열면 YouTube 탭이 잡혀 "집계 가능한 콘텐츠 데이터가 없습니다" 배너부터 본다.
// 데이터는 있는데 **없다고 오판**하게 되는 자리라, 기본값을 데이터가 있는 쪽으로 잡는다.
//
// 두 단계로 나뉘는 이유는 신호가 도착하는 시점이 다르기 때문이다.
//   1) `pickInitialPlatform` — 셸이 **첫 렌더 전에** 이미 갖고 있는 `channels[]` 로 찍는 추측.
//      여기엔 협찬 표본 정보가 없어서 "수집이 도는 채널 중 큰 곳" 이상은 알 수 없다.
//   2) `pickDataPlatform` — 첫 응답이 와서 **플랫폼별 표본 수**를 알게 된 뒤의 보정. 딱 한 번만 돈다.
// 1)만으로는 부족하고(아야킴은 YouTube 가 제일 크지만 표본 0), 2)만으로는 첫 화면이 흔들린다.
import type { ChannelPerformance, ChannelSubs, Platform } from '../creatorsApi'

/** 화이트리스트 — 계약 밖 social_type 이 섞여 들어와도 탭에 없는 값을 고르지 않는다. */
const KNOWN: Platform[] = ['youtube', 'instagram', 'tiktok']

/** 화면 탭 순서. 세 채널을 한 번에 그리는 자리(히어로 배지 등)가 이 순서를 따른다. */
export const ALL_PLATFORMS: Platform[] = KNOWN
const FALLBACK: Platform = 'youtube'

/** URL 파라미터처럼 밖에서 들어온 값이 계약 안의 플랫폼인지. 아니면 무시하고 기본 선택으로 간다. */
export function isPlatform(p: string | null | undefined): p is Platform {
  return p != null && (KNOWN as string[]).includes(p)
}

function isKnown(p: string): p is Platform {
  return (KNOWN as string[]).includes(p)
}

/**
 * 첫 렌더용 추측 — 셸의 `CreatorSummary.channels[]` 만 보고 정한다.
 *
 * 자동 수집이 도는 채널을 먼저 본다(`autoCollect` 미도달이면 `linked` 폴백). 수집이 안 도는 채널은
 * 팔로워만 있고 콘텐츠 지표가 영영 안 쌓이므로, 아무리 커도 기본값으로 삼으면 빈 화면이 된다.
 * 그 안에서는 구독자 최대 — 동률이면 화이트리스트 순서로 결정론적으로 자른다(렌더마다 흔들리면 안 된다).
 */
export function pickInitialPlatform(channels?: ChannelSubs[] | null): Platform {
  const usable = (channels ?? []).filter((c) => isKnown(c.platform))
  if (usable.length === 0) return FALLBACK
  const collecting = usable.filter((c) => c.autoCollect ?? c.linked)
  return best(collecting.length > 0 ? collecting : usable)
}

function best(channels: ChannelSubs[]): Platform {
  let top = channels[0]
  for (const c of channels.slice(1)) {
    const bigger = (c.subscribers ?? 0) > (top.subscribers ?? 0)
    const tie =
      (c.subscribers ?? 0) === (top.subscribers ?? 0) &&
      KNOWN.indexOf(c.platform) < KNOWN.indexOf(top.platform)
    if (bigger || tie) top = c
  }
  return top.platform
}

/**
 * 첫 응답 뒤 보정 — 지금 보고 있는 플랫폼에 표본이 0인데 다른 곳에 있으면 그쪽을 돌려준다.
 *
 * 바꿀 필요가 없으면 **null** 이다(호출부는 null 이면 아무것도 하지 않는다). 표본이 전부 0이면
 * 옮겨봐야 똑같이 비어 있으므로 그대로 둔다 — 근거 없이 탭을 흔들지 않는다.
 *
 * @param current 지금 선택된 플랫폼
 * @param samples 플랫폼별 표본 수. 값을 모르는 플랫폼은 넣지 않는다(0과 "모름"은 다르다).
 */
export function pickDataPlatform(
  current: Platform,
  samples: Partial<Record<Platform, number>>,
): Platform | null {
  if ((samples[current] ?? 0) > 0) return null
  let winner: Platform | null = null
  for (const p of KNOWN) {
    const n = samples[p] ?? 0
    if (n <= 0) continue
    if (winner == null || n > (samples[winner] ?? 0)) winner = p
  }
  return winner === current ? null : winner
}

/**
 * Overview 신호 — 채널 퍼포먼스는 **세 채널 전부**를 싣고 오므로 한 응답으로 판정이 끝난다.
 * 롱·숏을 합산한다(포맷 구분은 여기서 의미 없다 — "이 채널에 협찬 표본이 있나"만 본다).
 * `sampleSize` 미도달(구버전 BE)이면 그 플랫폼은 **넣지 않는다** — 0과 "모름"은 다르다.
 */
export function channelSamples(rows?: ChannelPerformance[] | null): Partial<Record<Platform, number>> {
  const out: Partial<Record<Platform, number>> = {}
  for (const r of rows ?? []) {
    if (!isKnown(r.platform) || !r.sampleSize) continue
    out[r.platform] = (r.sampleSize.long ?? 0) + (r.sampleSize.short ?? 0)
  }
  return out
}

// Performance 의 `coverage[]` 는 이 판정에 쓸 수 없다 — 보유 채널을 전부 담고 오지만
// **요청한 플랫폼 행만** 자기 소스로 채워지고 나머지는 bi_base_data 기준이라 IG·TikTok 이 늘 0이다.
// (같은 크리에이터가 YouTube 탭에선 "TikTok 수집 범위 밖", TikTok 탭에선 "TikTok 기준"으로 뒤집힌다.)
// 그래서 보정은 세 채널 표본이 전부 정확한 Overview 만 하고, 결과는 URL 로 전파한다.

/**
 * 그 채널이 왜 비었는가 — **세 가지는 완전히 다른 사실**이다.
 *
 * 탭은 늘 세 개가 뜨지만 크리에이터가 그 계정을 안 가졌을 수도, 계정은 있는데 수집 대상이
 * 아닐 수도 있다. 전부 "브랜디드 없음"으로 뭉뚱그리면 **없는 계정을 두고 "협찬을 안 했다"고
 * 말하는 셈**이라, 사용자가 크리에이터를 잘못 평가한다.
 *
 * 판정 입력은 `channels`(오디언스 스트립이 쓰는 그 목록) 하나뿐이라 추가 조회가 없다.
 *   · 목록에 없음        → 계정 자체가 없다
 *   · 있는데 수집 안 함  → 게시물이 영영 안 쌓인다(IG 는 전속·파트너 계약만 수집)
 *   · 그 외              → 수집은 되는데 브랜디드 판정이 없다
 */
export type ChannelEmptyState = 'no_account' | 'not_collected' | 'no_branded'

export function channelEmptyState(
  platform: Platform,
  channels?: ChannelSubs[] | null,
): ChannelEmptyState {
  const found = (channels ?? []).find((c) => c.platform === platform)
  if (!found) return 'no_account'
  return (found.autoCollect ?? found.linked) ? 'no_branded' : 'not_collected'
}

/** 화면 문구. 채널명을 넣어 "무엇이 없는지"를 문장 스스로 말하게 한다. */
export function channelEmptyText(platform: Platform, channels?: ChannelSubs[] | null): string {
  const label = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok' }[platform]
  switch (channelEmptyState(platform, channels)) {
    case 'no_account':
      return `${label} 계정이 등록되어 있지 않습니다`
    case 'not_collected':
      return `${label} 게시물은 수집 대상이 아닙니다`
    default:
      return '이 채널에는 브랜디드로 판정된 콘텐츠가 없습니다'
  }
}
